/* eslint-disable max-classes-per-file */

export class MaxNumberOfFilesError extends Error {
  constructor(message = "Maximum number of files exceeded.") {
    super(message);
    this.name = "MaxNumberOfFilesError";
    Error.captureStackTrace(this, this.constructor);
  }
}

export class FileTypeError extends Error {
  constructor(message = "Not an accepted File Type") {
    super(message);
    this.name = "FileTypeError";
    Error.captureStackTrace(this, this.constructor);
  }
}

export class FileSizeError extends Error {
  constructor(message = "Filesize is greater than our limit.") {
    super(message);
    this.name = "FileSizeError";
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UnhandledError extends Error {
  constructor(originalError: Error) {
    super(originalError.message, { cause: originalError });
    this.name = "UnhandledError";
    Error.captureStackTrace(this, this.constructor);
  }
}

export class CustomValidationError extends Error {
  constructor(message = "Custom validation failed") {
    super(message);
    this.name = "CustomValidationError";
    Error.captureStackTrace(this, this.constructor);
  }
}

/**
 * Raised before a file is added to the uploader when its name starts with
 * a leading underscore. The backend pipeline (Cloudinary/S3 + downstream
 * art-print rendering) does not support such names and silently stalls
 * uploads, so we surface this at the UI level per ART-8339.
 */
export class LeadingUnderscoreFilenameError extends Error {
  constructor(
    message = "File names cannot start with an underscore (\"_\"). Please rename the file and try again."
  ) {
    super(message);
    this.name = "LeadingUnderscoreFilenameError";
    Error.captureStackTrace(this, this.constructor);
  }
}

export type RestrictionsFailedError =
  | MaxNumberOfFilesError
  | FileTypeError
  | FileSizeError
  | LeadingUnderscoreFilenameError
  | UnhandledError;

export const getRestrictionsFailedError = (error: Error) => {
  const isMaxNumberOfFilesError = error.message.includes(
    "You can only upload "
  );
  const isFileTypeError = error.message.startsWith("You can only upload:");
  const isFileSizeError = error.message.includes(
    "exceeds maximum allowed size"
  );

  let customError: RestrictionsFailedError | null = null;

  if (isMaxNumberOfFilesError) {
    customError = new MaxNumberOfFilesError();
  }

  if (isFileTypeError) {
    customError = new FileTypeError();
  }

  if (isFileSizeError) {
    customError = new FileSizeError();
  }

  if (!customError) {
    customError = new UnhandledError(error);
  }

  return customError;
};
